/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
// Form controls and domain data structures have unsafe any usage
import { Injectable } from '@angular/core';
import { DomainRepoService } from '@client/app/shared-modules/api/services/domain-repo.service';
import { MON_USER_PERMISSIONS } from '@client/app/shared-modules/api/constants/user-permissions.constants';
import {
    MergedDomain,
    UserFormValue,
    UserData,
    UserDomainRequestItem,
    UserDomainGroupRequestItem,
    DomainApiResponse,
    DomainGroupApiResponse,
    UserUpdateRequest,
    UserCreateRequest,
} from '@client/app/feature-modules/admin/modules/admin-users/types/user.type';

@Injectable({
    providedIn: 'root',
})
export class UserFormService {
    private domainSelectionCache = new Map<number, { visible: boolean; sendReport: boolean; type?: 'domain' | 'domain_group' }>();
    private allDomainIdsCache: number[] | null = null;
    private allDomainIdsPromise: Promise<number[]> | null = null;
    private domainTypeCache = new Map<number, 'domain' | 'domain_group'>();
    private originalUserPermissions = new Map<number, { visible: boolean; sendReport: boolean; type: string }>();

    constructor (private domainRepoService: DomainRepoService) {}

    clearDomainSelectionCache (): void {
        this.domainSelectionCache.clear();
        this.allDomainIdsCache = null;
        this.domainTypeCache.clear();
        this.originalUserPermissions.clear();
    }

    saveDomainSelection (domainId: number, selection: { visible: boolean; sendReport: boolean; type?: 'domain' | 'domain_group' }): void {
        this.domainSelectionCache.set(domainId, selection);
    }

    getCachedDomainConfig (domainId: number): { visible: boolean; sendReport: boolean; type?: 'domain' | 'domain_group' } | undefined {
        return this.domainSelectionCache.get(domainId);
    }

    storeAllUserDomainPermissions (userData: UserData): void {
        const userDomainsMap = this.createUserDomainsMap(userData);
        const userDomainGroupsMap = this.createUserDomainGroupsMap(userData);

        this.originalUserPermissions.clear();

        // store domain users
        const domainUsers = Array.isArray(userData.domain_users) ? userData.domain_users : [];
        this.storePermissionsForType(domainUsers, 'domain', userDomainsMap, userDomainGroupsMap, 'domain_id');

        // store domain group members
        const domainGroupMembers = Array.isArray(userData.domain_group_members) ? userData.domain_group_members : [];
        this.storePermissionsForType(domainGroupMembers, 'domain_group', userDomainsMap, userDomainGroupsMap, 'domain_group_id');
    }

    private storePermissionsForType (
        items: (UserDomainRequestItem | UserDomainGroupRequestItem)[],
        type: 'domain' | 'domain_group',
        userDomainsMap: Map<number, UserDomainRequestItem>,
        userDomainGroupsMap: Map<number, UserDomainGroupRequestItem>,
        idField: string,
    ): void {
        if (!items) {
            return;
        }

        items.forEach((item: UserDomainRequestItem | UserDomainGroupRequestItem) => {
            const id = item[idField as keyof typeof item] as number;
            const config = this.getUserDomainConfig(id, type, userDomainsMap, userDomainGroupsMap, false);

            this.originalUserPermissions.set(id, {
                visible: config.visible,
                sendReport: config.sendReport,
                type,
            });

            this.saveDomainSelection(id, { ...config, type });
        });
    }

    getAllStoredPermissions (): Map<number, { visible: boolean; sendReport: boolean; type: string }> {
        return new Map(this.originalUserPermissions);
    }

    async getAllDomainIds (): Promise<number[]> {
        // return cached data if available
        if (this.allDomainIdsCache !== null) {
            return this.allDomainIdsCache;
        }

        try {
            const response = await this.domainRepoService.getIds();
            // extract both domain ids and domain group ids for bulk selection
            const allIds: number[] = [];

            response.forEach((domain: { id: number; domain_group_ids?: number[] }) => {
                // add the domain id and cache its type
                allIds.push(domain.id);
                this.domainTypeCache.set(domain.id, 'domain');

                // Add all domain group IDs and cache their types
                if (domain.domain_group_ids && domain.domain_group_ids.length > 0) {
                    domain.domain_group_ids.forEach(groupId => {
                        allIds.push(groupId);
                        this.domainTypeCache.set(groupId, 'domain_group');
                    });
                }
            });

            // cache the result
            this.allDomainIdsCache = allIds;
            return allIds;
        } catch (error) {
            return [];
        }
    }

    async ensureAllDomainIdsLoaded (): Promise<number[]> {
        if (this.allDomainIdsCache !== null) {
            return this.allDomainIdsCache;
        }
        if (this.allDomainIdsPromise) {
            return this.allDomainIdsPromise;
        }
        this.allDomainIdsPromise = this.domainRepoService.getIds()
            .then(response => {
                const allIds: number[] = [];
                response.forEach((domain: { id: number; domain_group_ids?: number[] }) => {
                    allIds.push(domain.id);
                    this.domainTypeCache.set(domain.id, 'domain');
                    if (domain.domain_group_ids && domain.domain_group_ids.length > 0) {
                        domain.domain_group_ids.forEach(groupId => {
                            allIds.push(groupId);
                            this.domainTypeCache.set(groupId, 'domain_group');
                        });
                    }
                });
                this.allDomainIdsCache = allIds;
                return allIds;
            })
            .catch(error => {
                this.allDomainIdsPromise = null;
                throw error;
            });
        return this.allDomainIdsPromise;
    }

    async loadUserDomains (userData: UserData, page: number, pageSize: number): Promise<{
        domains: MergedDomain[];
        total: number;
        perPage: number;
    }> {
        const domainsResponse = await this.domainRepoService.getAll({
            page,
            page_size: pageSize,
        });

        const userDomainsMap = this.createUserDomainsMap(userData);
        const userDomainGroupsMap = this.createUserDomainGroupsMap(userData);
        const isUserAdmin = Boolean(userData.customer_admin) || false;

        const mergedDomains = this.processDomainsResponse(domainsResponse, userDomainsMap, userDomainGroupsMap, isUserAdmin);

        return {
            domains: mergedDomains,
            total: domainsResponse.total || 0,
            perPage: domainsResponse.perPage || pageSize,
        };
    }

    async loadDomainsForNewUser (page: number, pageSize: number): Promise<{
        domains: MergedDomain[];
        total: number;
        perPage: number;
    }> {
        const domainsResponse = await this.domainRepoService.getAll({
            page,
            page_size: pageSize,
        });

        const mergedDomains = this.processDomainsResponse(domainsResponse);

        return {
            domains: mergedDomains,
            total: domainsResponse.total || 0,
            perPage: domainsResponse.perPage || pageSize,
        };
    }

    private processDomainsResponse (
        domainsResponse: DomainApiResponse[],
        userDomainsMap?: Map<number, UserDomainRequestItem>,
        userDomainGroupsMap?: Map<number, UserDomainGroupRequestItem>,
        isUserAdmin?: boolean,
    ): MergedDomain[] {
        const mergedDomains: MergedDomain[] = [];

        domainsResponse.forEach((domain: DomainApiResponse) => {
            // main domain
            const mainDomain = this.createMergedDomain(
                domain,
                'domain',
                userDomainsMap,
                userDomainGroupsMap,
                isUserAdmin,
            );
            mergedDomains.push(mainDomain);

            // domain groups
            if (domain.domain_groups && domain.domain_groups.length > 0) {
                domain.domain_groups.forEach((group: DomainGroupApiResponse) => {
                    const domainGroup = this.createMergedDomain(
                        group,
                        'domain_group',
                        userDomainsMap,
                        userDomainGroupsMap,
                        isUserAdmin,
                    );
                    mergedDomains.push(domainGroup);
                });
            }
        });

        return mergedDomains;
    }

    private createMergedDomain (
        item: DomainApiResponse | DomainGroupApiResponse,
        type: 'domain' | 'domain_group',
        userDomainsMap?: Map<number, UserDomainRequestItem>,
        userDomainGroupsMap?: Map<number, UserDomainGroupRequestItem>,
        isUserAdmin?: boolean,
    ): MergedDomain {
        const cachedConfig = this.getCachedDomainConfig(item.id);

        let finalPermissions;
        if (cachedConfig) {
            finalPermissions = cachedConfig;
        } else if (userDomainsMap && userDomainGroupsMap && isUserAdmin !== undefined) {
            finalPermissions = this.getUserDomainConfig(item.id, type, userDomainsMap, userDomainGroupsMap, isUserAdmin);
        } else {
            finalPermissions = { visible: false, sendReport: false };
        }

        return {
            id: item.id,
            title: item.title,
            visible: finalPermissions.visible,
            sendReport: finalPermissions.sendReport,
            type,
            originalData: item,
        };
    }

    createNewUserPayload (formValue: UserFormValue): UserCreateRequest {
        const { domain_users, domain_group_members } = this.transformDomainsForApi(
            formValue.domains || [],
            undefined,
        );

        const restrictedModules = this.getRestrictedModules(formValue.permissions || {});

        return {
            first_name: formValue.firstName,
            last_name: formValue.lastName,
            email: formValue.email,
            locale: formValue.interfaceLanguage || 'en',
            customer_admin: formValue.isAdmin || false,
            send_welcome_email: formValue.sendLoginInstructions || false,

            email_notifications: {
                exports: formValue.notifyOnExport || false,
            },

            settings: {
                modules: restrictedModules,
            },

            domain_users,
            domain_group_members,
        };
    }

    createUserUpdatePayload (formValue: UserFormValue, originalUserData: UserData): UserUpdateRequest {
        const { domain_users, domain_group_members } = this.transformDomainsForApi(
            formValue.domains || [],
            originalUserData,
        );

        const restrictedModules = this.getRestrictedModules(formValue.permissions || {});

        return {
            id: originalUserData.id as string | number,
            first_name: formValue.firstName,
            last_name: formValue.lastName,
            email: formValue.email,
            locale: formValue.interfaceLanguage || 'en',
            customer_admin: formValue.isAdmin || false,
            email_notifications: {
                exports: formValue.notifyOnExport || false,
            },
            settings: {
                modules: restrictedModules,
            },
            domain_users,
            domain_group_members,
        };
    }

    applyAdminVisibility (domains: MergedDomain[], isAdmin: boolean): MergedDomain[] {
        return domains.map(domain => ({
            ...domain,
            visible: isAdmin ? true : domain.visible,
        }));
    }

    private createUserDomainsMap (userData: UserData): Map<number, UserDomainRequestItem> {
        const domainUsers = Array.isArray(userData.domain_users) ? userData.domain_users : [];
        return this.createUserDomainEntityMap(domainUsers, 'domain_id');
    }

    private createUserDomainGroupsMap (userData: UserData): Map<number, UserDomainGroupRequestItem> {
        const domainGroupMembers = Array.isArray(userData.domain_group_members) ? userData.domain_group_members : [];
        return this.createUserDomainGroupEntityMap(domainGroupMembers, 'domain_group_id');
    }

    private createUserDomainEntityMap (entities: UserDomainRequestItem[], idField: string): Map<number, UserDomainRequestItem> {
        const map = new Map<number, UserDomainRequestItem>();
        if (entities) {
            entities.forEach((entity: UserDomainRequestItem) => {
                const id = entity[idField as keyof UserDomainRequestItem] as number;
                map.set(id, entity);
            });
        }
        return map;
    }

    private createUserDomainGroupEntityMap (entities: UserDomainGroupRequestItem[], idField: string): Map<number, UserDomainGroupRequestItem> {
        const map = new Map<number, UserDomainGroupRequestItem>();
        if (entities) {
            entities.forEach((entity: UserDomainGroupRequestItem) => {
                const id = entity[idField as keyof UserDomainGroupRequestItem] as number;
                map.set(id, entity);
            });
        }
        return map;
    }

    private getUserDomainConfig (
        domainId: number,
        type: 'domain' | 'domain_group',
        userDomainsMap: Map<number, UserDomainRequestItem>,
        userDomainGroupsMap: Map<number, UserDomainGroupRequestItem>,
        isUserAdmin: boolean,
    ): { visible: boolean; sendReport: boolean } {
        if (isUserAdmin) {
            const userAdminDomainConfig = type === 'domain'
                ? userDomainsMap.get(domainId)
                : userDomainGroupsMap.get(domainId);

            return {
                visible: true,
                sendReport: userAdminDomainConfig ? userAdminDomainConfig.send_report : false,
            };
        }

        const permissions = type === 'domain'
            ? userDomainsMap.get(domainId)
            : userDomainGroupsMap.get(domainId);

        if (permissions) {
            return {
                visible: permissions.visible,
                sendReport: permissions.send_report,
            };
        }

        return { visible: false, sendReport: false };
    }

    private transformDomainsForApi (domainsFormData: MergedDomain[], originalUserData?: UserData): {
        domain_users: UserDomainRequestItem[];
        domain_group_members: UserDomainGroupRequestItem[];
    } {
        const domain_users: UserDomainRequestItem[] = [];
        const domain_group_members: UserDomainGroupRequestItem[] = [];

        // create maps of existing domains and domain groups in user data
        const existingDomainUsers = new Map<number, UserDomainRequestItem>();
        const existingDomainGroups = new Map<number, UserDomainGroupRequestItem>();

        if (originalUserData) {
            const domainUsers = originalUserData.domain_users as UserDomainRequestItem[] | undefined;
            if (Array.isArray(domainUsers)) {
                domainUsers.forEach((domainUser: UserDomainRequestItem) => {
                    existingDomainUsers.set(domainUser.domain_id, domainUser);
                });
            }

            const domainGroupMembers = originalUserData.domain_group_members as UserDomainGroupRequestItem[] | undefined;
            if (Array.isArray(domainGroupMembers)) {
                domainGroupMembers.forEach((groupMember: UserDomainGroupRequestItem) => {
                    existingDomainGroups.set(groupMember.domain_group_id, groupMember);
                });
            }
        }

        // get all the cached domain IDs
        const cachedDomainIds = Array.from(this.domainSelectionCache.keys());

        // get all domain IDs from the user data
        const originalDomainIds: number[] = [];
        const originalDomainGroupIds: number[] = [];
        if (originalUserData) {
            const domainUsers = originalUserData.domain_users;
            if (Array.isArray(domainUsers)) {
                domainUsers.forEach((domainUser: UserDomainRequestItem) => {
                    originalDomainIds.push(domainUser.domain_id);
                });
            }
            const domainGroupMembers = originalUserData.domain_group_members;
            if (Array.isArray(domainGroupMembers)) {
                domainGroupMembers.forEach((groupMember: UserDomainGroupRequestItem) => {
                    originalDomainGroupIds.push(groupMember.domain_group_id);
                });
            }
        }

        // create a set of all domain IDs (current page + cached + user data)
        const currentPageIds = domainsFormData.map(domain => domain.id);
        const allUniqueIds = new Set([...currentPageIds, ...cachedDomainIds, ...originalDomainIds, ...originalDomainGroupIds]);

        allUniqueIds.forEach(domainId => {
            // Get cached selection first (from bulk operations)
            const cachedDomainConfig = this.getCachedDomainConfig(domainId);

            // Get current page domain data for type information
            const currentPageDomain = domainsFormData.find(d => d.id === domainId);

            // Check if this is an original domain/group from the user data
            const originalDomainUser = existingDomainUsers.get(domainId);
            const originalDomainGroup = existingDomainGroups.get(domainId);

            // priority: cached selection > user data > page data
            let permissions;
            if (cachedDomainConfig) {
                permissions = {
                    visible: cachedDomainConfig.visible,
                    sendReport: cachedDomainConfig.sendReport,
                };
            } else if (originalDomainUser) {
                // use existing permissions for domains
                permissions = {
                    visible: Boolean(originalDomainUser.visible),
                    sendReport: Boolean(originalDomainUser.send_report),
                };
            } else if (originalDomainGroup) {
                // use existing permissions for domain groups
                permissions = {
                    visible: Boolean(originalDomainGroup.visible),
                    sendReport: Boolean(originalDomainGroup.send_report),
                };
            } else {
                // fallback to current page data
                permissions = {
                    visible: currentPageDomain?.visible || false,
                    sendReport: currentPageDomain?.sendReport || false,
                };
            }

            // determine domain type - priority: cached selection > user data > page data
            let domainType = cachedDomainConfig?.type;
            if (!domainType) {
                if (originalDomainUser) {
                    domainType = 'domain';
                } else if (originalDomainGroup) {
                    domainType = 'domain_group';
                } else {
                    domainType = currentPageDomain?.type || this.inferDomainType(domainId);
                }
            }

            if (domainType === 'domain') {
                const existingDomainUser = existingDomainUsers.get(domainId);

                const domainUserEntry: UserDomainRequestItem = {
                    domain_id: domainId,
                    visible: permissions.visible,
                    send_report: permissions.sendReport,
                };

                // preserve user_id and id for any existing domain in the user data
                if (existingDomainUser) {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    domainUserEntry.user_id = existingDomainUser.user_id!;
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    domainUserEntry.id = existingDomainUser.id!;
                }

                domain_users.push(domainUserEntry);
            } else if (domainType === 'domain_group') {
                const existingDomainGroup = existingDomainGroups.get(domainId);

                const domainGroupEntry: UserDomainGroupRequestItem = {
                    domain_group_id: domainId,
                    visible: permissions.visible,
                    send_report: permissions.sendReport,
                };

                // preserve user_id and id for any existing domain groups in the user data
                if (existingDomainGroup) {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    domainGroupEntry.user_id = existingDomainGroup.user_id!;
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    domainGroupEntry.id = existingDomainGroup.id!;
                }

                domain_group_members.push(domainGroupEntry);
            }
        });

        return { domain_users, domain_group_members };
    }

    private inferDomainType (domainId: number): 'domain' | 'domain_group' {
        // check for cached type from getIds
        const cachedType = this.domainTypeCache.get(domainId);
        if (cachedType) {
            return cachedType;
        }

        // default to 'domain' if not cached
        return 'domain';
    }

    private getRestrictedModules (permissions: Record<string, boolean>): string[] {
        const restrictedModules: string[] = [];

        Object.keys(MON_USER_PERMISSIONS).forEach(key => {
            if (permissions[key] === false) {
                restrictedModules.push(key);
            }
        });

        return restrictedModules;
    }
}
